import { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { HistoryEntriesList, Input, StyledPageWrapper, Title } from "@monorepo/components";
import { latinizeString } from "@monorepo/helpers";
import { useGetHistoryBySearchQuery } from "@monorepo/services/api/history";
import { setSearchQuery } from "@monorepo/slices/history";
import theme from "@monorepo/styles/theme";
import { IHistoryEntry, IHistorySliceState } from "@monorepo/types";
import SearchIcon from "@mui/icons-material/Search";
import { Button, CircularProgress, Typography } from "@mui/material";
import { Box } from "@mui/system";
import debounce from "lodash.debounce";

const ENTRIES_PER_PAGE = 19;

const NoRecords = memo(() => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography textAlign="center" color={theme.palette.text.disabled}>
        {t("payment_history.no_records_found")}
      </Typography>
    </Box>
  );
});
NoRecords.displayName = "NoRecords";

const HistorySearch = ({ cartGroupId }: { cartGroupId: string }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { searchQuery = "" } = useSelector(({ historySlice }: IHistorySliceState) => historySlice);

  const [value, setValue] = useState<string>(searchQuery);
  const [page, setPage] = useState(1);
  const [debouncedValue, setDebouncedValue] = useState<string>(searchQuery);

  useEffect(() => {
    setPage(1);
  }, [debouncedValue]);

  const debounceHandler = debounce((v) => setDebouncedValue(v), 750);

  const { data: historyEntries } = useGetHistoryBySearchQuery();

  const isEntryMatchingSearch = useCallback(
    (entry: IHistoryEntry) => {
      if (!debouncedValue || debouncedValue.length < 3) return true;

      return latinizeString(String(entry.service_name).toLowerCase()).includes(
        latinizeString(String(debouncedValue).toLowerCase())
      );
    },
    [debouncedValue]
  );

  const thisCartEntries = useMemo(
    () => historyEntries?.filter((e) => String(e.cart_group_id) === cartGroupId),
    [historyEntries, cartGroupId]
  );

  const filteredEntries = useMemo(
    () => thisCartEntries?.filter((e) => isEntryMatchingSearch(e)) || [],
    [thisCartEntries, isEntryMatchingSearch]
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e?.target) return;

    const {
      target: { value: inputValue },
    } = e;

    setValue(inputValue);
    if (inputValue.length <= 0 || inputValue.length >= 3) {
      debounceHandler(inputValue);
    } else {
      setDebouncedValue(inputValue);
    }
  };

  const isSearching = value !== debouncedValue && value.length >= 3;

  return (
    <StyledPageWrapper sx={{ justifyContent: "unset" }}>
      <Title>{t("payment_history.label_full")}</Title>

      <Box
        sx={{
          width: "80%",
          margin: "auto",
          maxWidth: 340,
        }}
      >
        <Input
          label={t("history_filters.search_label")}
          value={value || ""}
          onChange={handleInputChange}
          icon={<SearchIcon sx={{ opacity: 0.2 }} />}
        />
      </Box>

      {isSearching ? (
        <CircularProgress sx={{ margin: "auto" }} />
      ) : !filteredEntries.length ? (
        <NoRecords />
      ) : (
        <>
          <HistoryEntriesList
            entries={filteredEntries.slice(0, page * ENTRIES_PER_PAGE)}
            entryDateFormat="yyyy MMMM dd'd'."
            cartGroupId={cartGroupId}
            onEntrySelect={() => dispatch(setSearchQuery(String(debouncedValue)))}
            cartLinkState={{
              fromFilters: true,
            }}
          />
          {filteredEntries.length > page * ENTRIES_PER_PAGE && (
            <Button
              disableElevation
              onClick={() => setPage((prev) => prev + 1)}
              variant="contained"
              sx={{ width: "fit-content", margin: "0 auto" }}
            >
              {t("payments.show_more")}
            </Button>
          )}
        </>
      )}
    </StyledPageWrapper>
  );
};

export default HistorySearch;
